import * as React from 'react'
import { Transition } from 'react-transition-group'
import {
  TransitionProps,
  getTransitionProps,
  reflow,
  createTransition,
} from '../transitions'
import { useForkRef } from '../../hooks'

export interface FadeProps extends Omit<TransitionProps, 'children'> {
  /**
   * Perform the enter transition when it first mounts if `in` is also `true`.
   * Set this to `false` to disable this behavior.
   * @default true
   */
  appear?: boolean
  /**
   * A single child content element.
   */
  children: React.ReactElement<any, any>
  /**
   * The transition timing function.
   * You may specify a single easing or a object containing enter and exit values.
   */
  easing?: TransitionProps['easing']
  /**
   * If `true`, the component will transition in.
   */
  in?: boolean
  /**
   * The duration for the transition, in milliseconds.
   * You may specify a single timeout for all transitions, or individually with an object.
   * @default {
   *   enter: theme.transitions.duration.enteringScreen,
   *   exit: theme.transitions.duration.leavingScreen,
   * }
   */
  timeout?: TransitionProps['timeout']
}

const styles = {
  entering: {
    opacity: 1,
  },
  entered: {
    opacity: 1,
  },
}

/**
 * The Fade transition is used by the [Modal] component.
 * It uses [react-transition-group](https://github.com/reactjs/react-transition-group) internally.
 */
export const Fade = React.forwardRef<HTMLDivElement, FadeProps>(
  function Fade(props, ref) {
    const defaultTimeout = {
      enter: 255, // refer to css variable --transitionDurationEnteringScreen
      exit: 195, // refer to css variable --transitionDurationLeavingScreen
    }

    const {
      addEndListener,
      appear = true,
      children,
      easing,
      in: inProp,
      onEnter,
      onEntered,
      onEntering,
      onExit,
      onExited,
      onExiting,
      style,
      timeout = defaultTimeout,
      ...other
    } = props

    const enableStrictModeCompat = true
    const nodeRef = React.useRef(null)
    // @ts-ignore
    const handleRef = useForkRef(nodeRef, children.ref, ref)

    const normalizedTransitionCallback =
      (callback: (node: HTMLElement, isAppearing?: boolean) => void) =>
      (maybeIsAppearing: boolean) => {
        if (callback) {
          const node = nodeRef.current!

          // onEnterXxx and onExitXxx callbacks have a different arguments.length value.
          if (maybeIsAppearing === undefined) {
            callback(node)
          } else {
            callback(node, maybeIsAppearing)
          }
        }
      }

    const handleEntering = normalizedTransitionCallback(onEntering as any)

    const handleEnter = normalizedTransitionCallback((node, isAppearing) => {
      reflow(node) // So the animation always start from the start.

      const transitionProps = getTransitionProps(
        { style, timeout, easing },
        {
          mode: 'enter',
        }
      )

      node.style.webkitTransition = createTransition('opacity', transitionProps)
      node.style.transition = createTransition('opacity', transitionProps)

      if (onEnter) {
        onEnter(node, isAppearing!)
      }
    })

    const handleEntered = normalizedTransitionCallback(onEntered as any)

    const handleExiting = normalizedTransitionCallback(onExiting as any)

    const handleExit = normalizedTransitionCallback(node => {
      const transitionProps = getTransitionProps(
        { style, timeout, easing },
        {
          mode: 'exit',
        }
      )

      node.style.webkitTransition = createTransition('opacity', transitionProps)
      node.style.transition = createTransition('opacity', transitionProps)

      if (onExit) {
        onExit(node)
      }
    })

    const handleExited = normalizedTransitionCallback(onExited as any)

    const handleAddEndListener = (next: any) => {
      if (addEndListener) {
        // Old call signature before `react-transition-group` implemented `nodeRef`
        addEndListener(nodeRef.current!, next)
      }
    }

    return (
      // @ts-ignore
      <Transition
        appear={appear}
        in={inProp}
        nodeRef={enableStrictModeCompat ? nodeRef : undefined}
        onEnter={handleEnter}
        onEntered={handleEntered}
        onEntering={handleEntering}
        onExit={handleExit}
        onExited={handleExited}
        onExiting={handleExiting}
        addEndListener={handleAddEndListener}
        timeout={timeout}
        {...other}
      >
        {(state: any, childProps: any) => {
          return React.cloneElement(children, {
            style: {
              opacity: 0,
              visibility: state === 'exited' && !inProp ? 'hidden' : undefined,
              // @ts-ignore
              ...styles[state],
              ...style,
              ...children.props.style,
            },
            ref: handleRef,
            ...childProps,
          })
        }}
      </Transition>
    )
  }
)
